// Shared theme-aware controls; loading, disabled and polymorphic link behavior remain feature-owned.
//
// Preline-aligned button system. Six variants × three sizes × two shapes,
// with caps-label and loading modes.
//
// 0.3.1 changes from 0.3.0:
//   - Default shape flipped from `pill` (rounded-full) to `rounded`
//     (rounded-md), matching Preline's standard 6px button corner.
//   - New `soft` variant: tinted background (indigo-50) + brand-colored
//     text (indigo-700) — for secondary CTAs that want brand presence
//     without the solid weight.
//   - Variant className uses new tokens (primary-hover, primary-soft-bg,
//     destructive-soft-bg) and standard 1px borders.
//
// `asChild` is the polymorphic escape hatch — `<Button asChild><Link
// to="/x">label</Link></Button>` renders a Link with all button styles,
// no extra wrapper.
//
// `loading` automatically forces disabled + adds a trailing spinner; the
// label fades to 70% opacity so it doesn't compete with the spinner.

import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'soft'
  | 'ghost'
  | 'destructive'
  | 'icon';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonShape = 'pill' | 'rounded';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Preline default is `rounded` (6px). Use `pill` for FAB / chip-style CTAs. */
  shape?: ButtonShape;
  /** Caps small-label override — `uppercase tracking-label text-label-sm`. */
  uppercase?: boolean;
  /** Render as the child element instead of a `<button>` (e.g. wrap a Link). */
  asChild?: boolean;
  /** Replaces children with a spinner + disables the button. */
  loading?: boolean;
}

const BASE = [
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
  'font-semibold transition-[background,opacity,transform,color,border-color] duration-200',
  'ease-spring active:scale-[0.98]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100',
].join(' ');

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  // md is the default and by far the most-used size; 44px meets the mobile
  // touch-target minimum app-wide (audit P1-4) so callers don't each override.
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
};

const SHAPE: Record<ButtonShape, string> = {
  pill: 'rounded-full',
  rounded: 'rounded-md',
};

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm',
  secondary:
    'border border-border bg-card text-foreground hover:bg-muted',
  // Soft = brand-tinted CTA that doesn't compete with primary; great for
  // secondary actions inside cards (e.g. "Verify →" inside StatCard).
  soft: 'bg-primary-soft-bg text-primary-soft-fg hover:bg-primary-soft-bg/80',
  ghost: 'bg-transparent text-foreground hover:bg-muted',
  destructive:
    'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm',
  // Icon hard-codes its own 44×44 round geometry (mobile touch-target min,
  // audit P1-4) — caller's size/shape ignored.
  icon: 'h-11 w-11 rounded-full p-0 text-ink-700 hover:bg-muted hover:text-foreground',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      variant = 'primary',
      size = 'md',
      shape = 'rounded',
      uppercase = false,
      asChild = false,
      loading = false,
      disabled,
      className,
      children,
      ...rest
    } = props;

    const isIcon = variant === 'icon';
    const Comp: React.ElementType = asChild ? Slot : 'button';

    const merged = cn(
      BASE,
      !isIcon && SIZE[size],
      !isIcon && SHAPE[shape],
      VARIANT[variant],
      uppercase && 'uppercase tracking-label text-label-sm',
      className,
    );

    return (
      <Comp
        ref={ref}
        className={merged}
        disabled={disabled || loading}
        {...rest}
      >
        {loading ? (
          <>
            <span className="opacity-70">{children}</span>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
